import { useEffect, useRef } from 'react';

function useRestoreInputFocus() {
    const lastFocusedInputRef = useRef(null);
    const shouldRestoreFocusRef = useRef(false);
    const restoreTimerRef = useRef(null);
    const restoreFrameRef = useRef(null);

    useEffect(() => {
        const isEditableInput = (target) => (
            target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement
        );

        const rememberFocusedInput = (event) => {
            const target = event.target;
            if (isEditableInput(target)) {
                lastFocusedInputRef.current = target;
            }
        };

        const markFocusForRestore = () => {
            const activeInput = document.activeElement;
            if (isEditableInput(activeInput)) {
                lastFocusedInputRef.current = activeInput;
            }

            if (lastFocusedInputRef.current?.isConnected) {
                shouldRestoreFocusRef.current = true;
            }
        };

        const restoreFocus = () => {
            if (!shouldRestoreFocusRef.current || document.visibilityState !== 'visible') {
                return;
            }

            const input = lastFocusedInputRef.current;

            if (!input?.isConnected || input.disabled || input.readOnly) {
                shouldRestoreFocusRef.current = false;
                return;
            }

            window.clearTimeout(restoreTimerRef.current);
            restoreTimerRef.current = window.setTimeout(() => {
                if (
                    document.visibilityState !== 'visible' ||
                    !input.isConnected ||
                    input.disabled ||
                    input.readOnly
                ) {
                    return;
                }

                if (document.activeElement === input) {
                    input.blur();
                }

                window.cancelAnimationFrame(restoreFrameRef.current);
                restoreFrameRef.current = window.requestAnimationFrame(() => {
                    input.focus({ preventScroll: true });

                    if (typeof input.setSelectionRange === 'function') {
                        const cursorPosition = input.value.length;
                        input.setSelectionRange(cursorPosition, cursorPosition);
                    }

                    shouldRestoreFocusRef.current = false;

                    if (typeof navigator.virtualKeyboard?.show === 'function') {
                        try {
                            navigator.virtualKeyboard.show();
                        } catch (e) {
                            // Keyboard display is best-effort across Android Chrome versions.
                        }
                    }
                });
            }, 100);
        };

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'hidden') {
                markFocusForRestore();
                return;
            }

            restoreFocus();
        };

        document.addEventListener('focusin', rememberFocusedInput);
        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('blur', markFocusForRestore);
        window.addEventListener('focus', restoreFocus);

        return () => {
            document.removeEventListener('focusin', rememberFocusedInput);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('blur', markFocusForRestore);
            window.removeEventListener('focus', restoreFocus);
            window.clearTimeout(restoreTimerRef.current);
            window.cancelAnimationFrame(restoreFrameRef.current);
        };
    }, []);
}

export default useRestoreInputFocus;
